<template>
  <div 
    ref="containerRef"
    class="confirmation-container d-flex align-items-center justify-content-center"
    :class="{ 'compact-view': asistenciaConfirmada }"
  >
    <div 
      class="confirmation-card p-4 p-md-5 rounded-4 shadow-lg"
      :class="{ 'compact-card': asistenciaConfirmada }"
      ref="formCardRef"
    >
      <!-- MENSAJE FINAL -->
      <div v-if="asistenciaConfirmada" class="qr-view text-center">
        <p class="qr-header">¡Gracias!</p>
        <p class="qr-instructions">
          Gracias por confirmar tu asistencia.
        </p>
      </div>

      <!-- FORMULARIO -->
      <div v-else>
        <div class="form-header">
          <h2 ref="tituloAnimado" class="form-title">
            Confirmar Asistencia
          </h2>
          <br>

          <div class="progress-container">
            <div
              class="progress-bar"
              :style="{ width: ((currentStep - 1) / (totalSteps - 1)) * 100 + '%' }"
            ></div>
          </div>
          <p class="form-subtitle">Paso {{ currentStep }} de {{ totalSteps }}</p>
        </div>

        <form @submit.prevent="handleSubmit" class="confirmation-form">
          <!-- Paso 1 -->
          <div v-if="currentStep === 1" class="step-box">
            <div class="form-group">
              <label class="form-label">Nombre</label>
              <input
                v-model.trim="name"
                type="text"
                class="form-control"
                placeholder="Escribe tu nombre"
                required
              >
            </div>
            <div class="form-group">
              <label class="form-label">Teléfono</label>
              <input
                v-model.trim="phone"
                type="tel"
                class="form-control"
                placeholder="Celular a 10 dígitos"
                required
              >
            </div>
          </div>

          <!-- Paso 2 -->
          <div v-if="currentStep === asistenciaStep" class="step-box">
            <label class="form-label">¿Asistirás?</label>
            <div class="radio-group">
              <label><input type="radio" value="si" v-model="attending"> Sí</label>
              <label><input type="radio" value="no" v-model="attending"> No</label>
            </div>
          </div>

          <!-- Paso 3 -->
          <div v-if="currentStep === totalSteps && attending !== 'no'" class="step-box">
            <label class="form-label">¿Cuántos asistirán?</label>
            <select v-model.number="guests" class="form-select" required>
              <option :value="null" disabled>Selecciona una opción</option>
              <option v-for="n in guestsOptions" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>

          <!-- Botones -->
          <div class="d-flex justify-content-between mt-3 buttons-row">
            <button
              v-if="currentStep > 1"
              type="button"
              class="submit-button secondary"
              @click="prevStep"
            >
              Atrás
            </button>

            <button
              v-if="currentStep < totalSteps"
              type="button"
              class="submit-button"
              @click="nextStep"
            >
              Siguiente
            </button>

            <button
              v-if="currentStep === totalSteps"
              type="submit"
              class="submit-button"
            >
              Confirmar
            </button>
          </div>

          <div v-if="errorMessage" class="alert alert-danger mt-3">
            {{ errorMessage }}
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, nextTick } from 'vue'
import axios from 'axios'
import baseUrl from '@/api'
import gsap from 'gsap'

export default {
  name: 'AttendanceConfirmation',
  props: {
    idInvitado: Number,
    nombre: String,
    idFestejado: Number
  },
  setup(props) {
    const name = ref('')
    const phone = ref('')
    const attending = ref('')
    const guests = ref(null)
    const guestsOptions = ref([1,2,3,4,5,6])
    const asistenciaConfirmada = ref(false)
    const currentStep = ref(1)
    const errorMessage = ref('')

    const formCardRef = ref(null)
    const tituloAnimado = ref(null)
    const containerRef = ref(null)

    const isValidInvitado = computed(() => Number(props.idInvitado) > 0)

    const totalSteps = computed(() => 3)
    const asistenciaStep = computed(() => 2)

    const nextStep = () => {
      errorMessage.value = ''

      if (currentStep.value === 1) {
        if (!name.value || !phone.value) {
          errorMessage.value = 'Escribe tu nombre y número de teléfono'
          return
        }
      }

      if (currentStep.value === asistenciaStep.value) {
        if (!attending.value) {
          errorMessage.value = 'Selecciona si asistirás o no'
          return
        }
        if (attending.value === 'no') {
          currentStep.value = totalSteps.value
          return
        }
      }

      if (currentStep.value === totalSteps.value && attending.value === 'si') {
        if (!guests.value) {
          errorMessage.value = 'Selecciona cuántos asistirán'
          return
        }
      }

      currentStep.value++
    }

    const prevStep = () => {
      errorMessage.value = ''
      currentStep.value--
    }

    const handleSubmit = async () => {
      try {
        const payload = {
          nombre_asistencia: name.value,
          telefono_invitado: phone.value,
          asistira: attending.value,
          num_pases: attending.value === 'no' ? 0 : guests.value,
          id_festejada: props.idFestejado,
          invitedby: isValidInvitado.value ? (props.nombre || 'xv') : 'xv'
        }

        if (isValidInvitado.value) {
          payload.id_invitado = props.idInvitado
        }

        await axios.post(`${baseUrl}/asistencia`, payload)
        asistenciaConfirmada.value = true
      } catch (e) {
        errorMessage.value = 'Error al confirmar la asistencia'
      }
    }

    onMounted(async () => {
      if (!isValidInvitado.value) {
        guestsOptions.value = [1,2,3,4,5,6]
      }

      await nextTick()

      gsap.fromTo(
        formCardRef.value,
        { opacity: 0, y: 40, scale: 0.96 },
        { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: 'power2.out' }
      )

      gsap.fromTo(
        tituloAnimado.value,
        { y: -20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5 }
      )
    })

    return {
      name,
      phone,
      attending,
      guests,
      guestsOptions,
      asistenciaConfirmada,
      currentStep,
      totalSteps,
      asistenciaStep,
      nextStep,
      prevStep,
      handleSubmit,
      errorMessage,
      formCardRef,
      tituloAnimado,
      containerRef
    }
  }
}
</script>

<style scoped>
.confirmation-container {
  min-height: 70vh;
  padding: 2rem 1rem;
  background: radial-gradient(circle at top, rgba(143,187,223,0.12), transparent 55%);
  transition: all 0.3s ease;
  position: relative;
}

.confirmation-card {
  width: 100%;
  max-width: 500px;
  background: linear-gradient(to bottom, #ffffff, #f9f9f9);
  border: 1px solid #e0e0e0;
  border-radius: 1.5rem;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.12);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.confirmation-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 22px 50px rgba(0, 0, 0, 0.16);
}

/* Vista compacta para QR */
.compact-view {
  min-height: auto !important;
  align-items: flex-start;
  padding-top: 4rem;
}

.compact-card {
  max-width: 400px;
  padding: 2rem;
}

/* Encabezado del formulario */
.form-header {
  margin-bottom: 2rem;
  text-align: center;
}

.form-title {
  font-family: 'Playfair Display', serif;
  font-size: 25px;
  font-family: 'Playfair Display', serif;
  color: #57554d;  margin-bottom: 0.5rem;
}

.form-title .subtitulo {
  font-size: 2.4rem;
}

.form-subtitle {
  font-family: 'Playfair Display', serif;
  color: #57554d;
  font-size: 0.95rem;
}

/* Grupos de formulario */
.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-family: 'Playfair Display', serif;
  font-weight: 500;
  color: #495057;
}

/* Controles de formulario */
.form-control,
.form-select {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #ced4da;
  border-radius: 0.75rem;
  background-color: rgba(255, 255, 255, 0.9);
  transition: border-color 0.3s, box-shadow 0.3s, transform 0.1s;
}

.form-control:focus,
.form-select:focus {
  border-color: #6c625c;
  box-shadow: 0 0 0 0.15rem rgba(143, 187, 223, 0.35);
  outline: none;
  transform: translateY(-1px);
}

/* Radio buttons */
.radio-group {
  display: flex;
  gap: 1.5rem;
}

.radio-option {
  display: flex;
  align-items: center;
}

.radio-input {
  margin-right: 0.5rem;
  accent-color: #495057;
}

.radio-label {
  font-family: 'Playfair Display', serif;
  color: #495057;
}

/* Botones */
.buttons-row {
  gap: 0.75rem;
}

.submit-button {
  flex: 1;
  padding: 0.75rem;
  font-family: 'Playfair Display', serif;
  font-size: 1.25rem;
  color: #f9f9f9;
  background: rgba(161, 151, 142, 0.95);  /* Color oscuro */
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.15s, box-shadow 0.15s;
  box-shadow: 0 8px 18px rgba(143, 187, 223, 0.45);
}

.submit-button.secondary {
  background-color: #282726;
  color: #ffffff;
  box-shadow: none;
}

.submit-button:hover:not(:disabled) {
  background-color: #282726;
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(82, 85, 88, 0.6);
}

.submit-button.secondary:hover {
  background-color: #e1e7f0;
}

.submit-button:disabled {
  background-color: #282726;
  opacity: 0.7;
  cursor: not-allowed;
}

/* Vista QR */
.qr-view {
  padding: 1rem;
}

.qr-header {
  font-family: 'padres', cursive;
  font-size: 1.75rem;
  color: #282726;
  margin-bottom: 1.5rem;
}

.confirmation-thanks {
  font-family: 'Karla', sans-serif;
  font-weight: 600;
  color: #282726;
  font-size: 1.25rem;
  margin-bottom: 1rem;
}

.qr-wrapper {
  margin: 1rem 0;
}

.qr-code {
  width: 190px;
  height: 190px;
  border: 8px solid white;
  border-radius: 1rem;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.16);
}

.qr-instructions {
  font-family: 'Karla', sans-serif;
  color: #282726;
  font-size: 0.95rem;
  margin-top: 1rem;
}

/* Mensajes de error */
.alert {
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
}
/* Botones */
.buttons-row {
  gap: 0.75rem;
}

.submit-button {
  flex: 1;
  padding: 0.75rem;
  font-family: 'Playfair Display', serif;
  font-size: 1.25rem;
  color: #f9f9f9;
  background-color: #817b76;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.15s, box-shadow 0.15s;
  box-shadow: 0 8px 18px rgba(143, 187, 223, 0.45);
}

.submit-button.secondary {
  background-color: #f3f5f9;
  color: #817b76;
  box-shadow: none;
}

.submit-button:hover:not(:disabled) {
  background-color: #817b76;
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(55, 55, 56, 0.6);
}

.submit-button.secondary:hover {
  background-color: #817b76;
}

.submit-button:disabled {
  background-color: #282726;
  opacity: 0.7;
  cursor: not-allowed;
}


.qr-error-message {
  color: #dc3545;
  font-weight: 500;
}

.loading-indicator {
  font-size: 0.875rem;
  color: #7d726c;
  margin-top: 0.5rem;
}

/* Tooltip recordatorio */
.tooltip-reminder {
  position: fixed;
  bottom: 18px;
  right: 18px;
  padding: 0.6rem 1.2rem;
  font-family: 'Karla', sans-serif;
  font-size: 0.9rem;
  color: #282726;
  z-index: 50;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

/* Progress bar */
.progress-container {
  height: 8px;
  margin-top: 40px;
  background-color: #e0e0e0;
  border-radius: 999px;
  margin-bottom: 1rem;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #282726, #524e50);
  transition: width 0.4s ease;
}

/* Responsive */
@media (max-width: 576px) {
  .confirmation-card {
    padding: 1.5rem;
  }
  
  .form-title {
    font-size: 1.9rem;
  }
  
  .radio-group {
    gap: 1rem;
  }

  .tooltip-reminder {
    bottom: 12px;
    right: 12px;
    font-size: 0.8rem;
  }
}
/* Vista confirmación compacta */
.compact-success{
  padding: 0.5rem 0 !important;
}

.compact-card{
  padding: 1.5rem !important;
  max-width: 320px !important;
}

.qr-header{
  margin-bottom: 0.3rem;
  font-size: 1.5rem;
}

.confirmation-thanks{
  font-size: 1rem;
  margin: 0;
}
</style>
